/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ChangeEvent } from "react";
import React from "react";
// import { Textarea } from '../textarea'
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import type { ISurveyOption } from "@/model/survey";

const OtherInput = React.forwardRef<
  HTMLInputElement,
  {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
  }
>(function OtherInput(
  { value, onChange, placeholder = "Enter other input" },
  ref,
) {
  return (
    // 3.3.2 Labels or Instructions — A: placeholder-only field, so a screen reader
    // announced an unlabelled edit box for the free-text survey answer.
    <Input
      ref={ref}
      aria-label={placeholder}
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      maxLength={100}
      className="h-[20px] w-2/5 rounded-sm bg-primary-extraLight"
    />
  );
});

export const QuestionLabel = ({
  questionNo,
  questionText,
  isRequired,
  hasError = false,
}: {
  questionNo: number;
  questionText: string;
  isRequired: boolean;
  hasError?: boolean;
}) => {
  return (
    <Label
      htmlFor={`question_${questionNo}`}
      className="text-base font-semibold"
    >
      {questionNo}. {questionText}{" "}
      {isRequired && (
        <span className={hasError ? "text-red-500" : "text-black"}>*</span>
      )}
    </Label>
  );
};

export const QuestionTypeParagraph = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
    // console.log("paragraph type answer >", value);
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    onChange(value);
  };

  return (
    // 3.3.2 Labels or Instructions — A: placeholder-only field.
    <Textarea
      ref={inputRef}
      aria-label="Please write your answer"
      value={value}
      rows={1}
      maxLength={500}
      placeholder="Please write your answer"
      className="resize-none rounded bg-primary-extraLight"
      // className="resize-none border-0 border-b-2 border-b-[#576574] rounded-none text-[#576574] mt-[1.8vh] focus:outline-none transition duration-150 ease-in-out scrollbar-none"
      onChange={handleInputChange}
    />
  );
};

export const QuestionTypeCheckbox = ({
  options,
  value,
  onChange,
  isReadOnly = false,
}: {
  options: ISurveyOption[];
  value: string[];
  onChange: (value: string[]) => void;
  isReadOnly?: boolean;
}) => {
  const [otherValue, setOtherValue] = React.useState("");
  const otherInputRef = React.useRef<HTMLInputElement>(null);

  // Sync otherValue only in read-only mode
  React.useEffect(() => {
    if (isReadOnly) {
      const otherOptionValue = value.find((opt) => opt.startsWith("Other:"));
      if (otherOptionValue) {
        const otherValue = otherOptionValue.split("Other:")[1];
        setOtherValue(otherValue.trim());
      }
    }
  }, [isReadOnly, value]);

  // console.log("values in checkbox component >", value);

  const otherOption = options.find((opt: any) => opt.isOther);

  const handleCheckboxChange = (option: string, checked: boolean) => {
    // console.log("change option > ", option, "checked >", checked);

    const newValue = checked
      ? [...value, option]
      : value.filter((item) => {
          if (option.startsWith("Other")) {
            return !item.startsWith("Other");
          }
          return item !== option;
        });
    if (!checked && option?.startsWith("Other")) {
      setOtherValue("");
    }
    onChange(newValue);

    // Focus the input when "Other..." is checked
    if (checked && option === otherOption?.option && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  };

  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOtherValue = e.target.value;
    setOtherValue(newOtherValue);
    // Remove any existing "Other" values
    const newValue = value.filter((item) => !item.startsWith("Other"));
    // Add the "Other" option's value to check the checkbox
    if (newOtherValue && otherOption && !value.includes(otherOption.option)) {
      // Add the custom "Other" value if provided
      if (newOtherValue) {
        newValue.push(`Other:${newOtherValue}`);
      }
    }
    onChange(newValue);
  };

  return (
    <div className="flex flex-col space-y-3">
      {options?.map((option: ISurveyOption, index: number) => (
        <div key={option?.id} className="flex items-center space-x-3">
          <Checkbox
            id={`option_${option?.id}_${index}`}
            checked={
              value?.includes(option?.option) ||
              (option.isOther &&
                value?.some((item) => item?.startsWith("Other")))
            }
            onCheckedChange={(checked) =>
              handleCheckboxChange(option.option, checked as boolean)
            }
          />
          <Label
            htmlFor={`option_${option?.id}_${index}`}
            className="align-baseline"
          >
            {option?.label}
          </Label>
          {option?.isOther && (
            <OtherInput
              ref={otherInputRef}
              value={otherValue}
              onChange={handleOtherChange}
              placeholder="Enter other option"
            />
          )}
        </div>
      ))}
    </div>
  );
};

export const QuestionTypeRadio: React.FC<{
  options: ISurveyOption[];
  value: string;
  onChange: (value: string) => void;
  isReadOnly?: boolean;
}> = ({ options, value, onChange, isReadOnly = false }) => {
  const [otherValue, setOtherValue] = React.useState("");
  const otherInputRef = React.useRef<HTMLInputElement>(null);

  // Sync otherValue only in read-only mode
  React.useEffect(() => {
    if (isReadOnly && value.startsWith("Other:")) {
      const otherValue = value.slice("Other:".length).trim();
      setOtherValue(otherValue);
    }
  }, [isReadOnly, value]);

  // console.log("value in radiobutton >", value, "other value >", otherValue);

  // Find the "Other" option to use its option value
  const otherOption = options.find((opt) => opt.isOther);

  const handleRadioChange = (newValue: string) => {
    // Clear otherValue if a non-"Other" option is selected
    if (newValue !== (otherOption?.option || "Other...")) {
      setOtherValue("");
    }
    onChange(newValue);

    if (newValue && newValue === otherOption?.option && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  };

  const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOtherValue = e.target.value;
    setOtherValue(newOtherValue);
    // Select the "Other" radio button when typing starts
    const otherOptionValue = otherOption?.option || "Other...";
    onChange(newOtherValue ? `Other:${newOtherValue}` : otherOptionValue);
  };

  return (
    <RadioGroup
      value={
        value?.startsWith("Other:") ? otherOption?.option || "Other..." : value
      }
      onValueChange={handleRadioChange}
      className="flex flex-col space-y-2"
    >
      {options.map((option: ISurveyOption, _index: number) => (
        <div key={option.id} className="flex items-center space-x-3">
          <RadioGroupItem
            value={option.option}
            id={`${option.id}-${option.option}`}
          />
          <Label
            htmlFor={`${option.id}-${option.option}`}
            className="align-baseline"
          >
            {option?.label}
          </Label>
          {option?.isOther && (
            <OtherInput
              ref={otherInputRef}
              value={otherValue}
              onChange={handleOtherChange}
              placeholder="Enter other option"
            />
          )}
        </div>
      ))}
    </RadioGroup>
  );
};

export const QuestionTypeLinearScale: React.FC<{
  options: ISurveyOption[];
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => {
  // console.log("values in linear scale >", value);
  return (
    <div className="w-2/5 justify-center">
      <RadioGroup
        value={value}
        onValueChange={onChange}
        className="flex items-center justify-between px-2"
      >
        {options.map((option: any) => (
          <div key={option.id} className="flex flex-col items-center space-y-1">
            <Label
              htmlFor={`${option.id}-${option.value}`}
              className="text-sm text-muted-foreground"
            >
              {option.option}
            </Label>
            <RadioGroupItem
              value={option.option}
              id={`${option.id}-${option.option}`}
            />
          </div>
        ))}
      </RadioGroup>
      {/* Labels for the start and end of the scale */}
      <div className="mt-2 flex justify-between text-xs text-muted-foreground">
        <span>{options[0].label}</span>
        <span>{options[options.length - 1].label}</span>
      </div>
    </div>
  );
};
